import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export type SortOrder = 'asc' | 'desc' | null;

interface SortableHeaderProps {
    label: string;
    field: string;
    currentField: string | null;
    currentOrder: SortOrder;
    onSort: (field: string, order: SortOrder) => void;
    className?: string;
}

/**
 * SortableHeader — Click-to-sort column header for ERP list tables.
 * Cycles: none → asc → desc → none
 */
export function SortableHeader({
    label, field, currentField, currentOrder, onSort, className,
}: SortableHeaderProps) {
    const isActive = currentField === field;

    const handleClick = () => {
        if (!isActive) {
            onSort(field, 'asc');
        } else if (currentOrder === 'asc') {
            onSort(field, 'desc');
        } else {
            onSort(field, null);
        }
    };

    return (
        <th
            className={cn(
                'px-2 py-1.5 text-left cursor-pointer select-none hover:bg-black/5 transition-colors group whitespace-nowrap',
                isActive && 'apv-sort-active',
                className,
            )}
            onClick={handleClick}
        >
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
                {label}
                {isActive ? (
                    currentOrder === 'asc'
                        ? <ArrowUp className="h-3 w-3 apv-accent-text shrink-0" />
                        : <ArrowDown className="h-3 w-3 apv-accent-text shrink-0" />
                ) : (
                    <ArrowUpDown className="h-3 w-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                )}
            </span>
        </th>
    );
}

/**
 * useTableSort — Simple client-side sort hook.
 * Sorts an array by a field in asc/desc order.
 */
import { useState, useMemo } from 'react';

export function useTableSort<T extends Record<string, any>>(data: T[]) {
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<SortOrder>(null);

    const handleSort = (field: string, order: SortOrder) => {
        if (!order) {
            setSortField(null);
            setSortOrder(null);
        } else {
            setSortField(field);
            setSortOrder(order);
        }
    };

    const sorted = useMemo(() => {
        if (!sortField || !sortOrder) return data;
        return [...data].sort((a, b) => {
            let va = a[sortField];
            let vb = b[sortField];

            // Handle nested fields like 'customer.name'
            if (sortField.includes('.')) {
                const parts = sortField.split('.');
                va = parts.reduce((obj: any, key) => obj?.[key], a);
                vb = parts.reduce((obj: any, key) => obj?.[key], b);
            }

            // Nulls last
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;

            // Numbers
            if (typeof va === 'number' && typeof vb === 'number') {
                return sortOrder === 'asc' ? va - vb : vb - va;
            }

            // Dates
            if (va instanceof Date || (typeof va === 'string' && !isNaN(Date.parse(va)))) {
                const da = new Date(va).getTime();
                const db = new Date(vb).getTime();
                if (!isNaN(da) && !isNaN(db)) {
                    return sortOrder === 'asc' ? da - db : db - da;
                }
            }

            // Strings
            const sa = String(va).toLowerCase();
            const sb = String(vb).toLowerCase();
            return sortOrder === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
        });
    }, [data, sortField, sortOrder]);

    return { sorted, sortField, sortOrder, handleSort };
}
